'use client';

import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname } from '@/i18n/navigation';
import Container from '../container';
import { pages } from '@/constants/pages';
import useCategoryStore from '@/store/category.store';
import { findObjectById, modifyMenuTreeData } from '@/utils/global';
import ArrowIcon from '../icons/arrow-icon';
import NextLink from '@/components/next-link';

const Breadcrumb = ({ current, categoryId, parentCategory = null }) => {
  const pathname = usePathname()?.slice(1);
  const locale = useLocale();
  const t = useTranslations('pages');
  const isFixedBreadcrumb = pages[pathname];
  const { categoryTreeData } = useCategoryStore((state) => state);
  const categories = categoryId
    ? modifyMenuTreeData(categoryTreeData?.children_data)
    : null;

  const [breadcrumbItems, setBreadcrumbItems] = useState([]);

  useEffect(() => {
    if (!categoryId || !Array.isArray(categories) || !categories.length) return;

    const pathSegments = pathname.split('/').filter(Boolean);

    const newItems = pathSegments
      .map((segment) => {
        const id = segment.slice(segment.lastIndexOf('-c-') + 3);
        const matched = findObjectById(categories, 'id', id);
        return matched
          ? { name: matched.name, link: matched.currentURL }
          : null;
      })
      .filter(Boolean);

    setBreadcrumbItems((prev) =>
      JSON.stringify(prev) === JSON.stringify(newItems) ? prev : newItems
    );
  }, [categoryId, pathname, categories]);

  const renderArrow = () => (
    <ArrowIcon
      className={`mt-0.5 max-h-3 min-h-3 max-w-3 min-w-3 ${locale.includes('ar') ? 'rotate-90' : '-rotate-90'}`}
    />
  );

  const renderBreadcrumbItem = (item, idx, isLast) => (
    <div key={idx} className='flex items-center gap-1'>
      {renderArrow()}
      {isLast ? (
        <span className='font-bold whitespace-nowrap text-red-500 max-md:text-sm max-sm:text-xs'>
          {item.name}
        </span>
      ) : (
        <NextLink
          href={item.link}
          className='hover:text-primary font-bold whitespace-nowrap max-md:text-sm max-sm:text-xs'
        >
          {item.name}
        </NextLink>
      )}
    </div>
  );

  return (
    <section className='bg-zinc-50 py-1'>
      <Container>
        <div className='mx-auto flex items-center gap-2 overflow-x-auto overflow-y-hidden'>
          <NextLink
            href='/'
            className='font-bold whitespace-nowrap max-md:text-sm max-sm:text-xs'
          >
            {locale?.includes('en') ? 'Home' : 'الرئيسية'}
          </NextLink>

          {parentCategory?.length
            ? parentCategory?.slice(0, 1).map((cat, idx) => {
                const link = parentCategory
                  ?.slice(0, idx + 1)
                  .map((item) => item?.url)
                  .join('/');
                return (
                  <div
                    key={`product-parent-category-${link}`}
                    className='hover:text-primary flex items-center gap-2'
                  >
                    {renderArrow()}
                    <NextLink
                      className='hover:text-primary max-w-max min-w-max font-bold whitespace-nowrap max-md:text-sm max-sm:text-xs'
                      href={`/${link}`}
                    >
                      {cat?.name}
                    </NextLink>
                  </div>
                );
              })
            : breadcrumbItems.map((item, idx) =>
                renderBreadcrumbItem(
                  item,
                  idx,
                  idx === breadcrumbItems.length - 1 &&
                    !current &&
                    !isFixedBreadcrumb
                )
              )}

          {current && (
            <div className='flex items-center gap-1'>
              {breadcrumbItems.length > 0 || parentCategory?.length > 0
                ? renderArrow()
                : renderArrow()}
              <span
                className={`font-bold whitespace-nowrap text-red-500 max-md:text-sm max-sm:text-xs`}
              >
                {current}
              </span>
            </div>
          )}

          {!current && isFixedBreadcrumb && (
            <div className='flex items-center gap-1'>
              {breadcrumbItems.length > 0 || parentCategory?.length > 0
                ? renderArrow()
                : renderArrow()}
              <span className='font-bold whitespace-nowrap text-red-500 max-md:text-sm max-sm:text-xs'>
                {t(pages[pathname])}
              </span>
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};

export default Breadcrumb;
